import { useState, useMemo } from "react"
import { Link } from "react-router"
import { ArrowLeft, Plus, CreditCard, Landmark } from "lucide-react"
import { formatAmount } from '@/lib/utils'
import { UNCATEGORIZED_LABEL } from '@/lib/constants'
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { toast } from "sonner"
import { useCategories, useRecurring, useRecurringSort, useRecurringTab, useUpdateRecurring, useUsdToJpyRate, usePaymentMethods } from "@/lib/hooks"
import { convertUsdToJpy } from "@/lib/exchange-rate"
import type { RecurringSortKey } from "@/lib/hooks"
import type { RecurringTransaction, PaymentMethod } from "@/lib/hooks"
import { RecurringEditSheet } from '@/components/recurring/recurring-edit-sheet'
import { DeleteRecurringDialog } from '@/components/recurring/delete-recurring-dialog'
import { getNextOccurrenceDate } from '@/lib/recurring-date'

function RecurringListItem({ item, onEdit, paymentMethod }: { item: RecurringTransaction; onEdit: (item: RecurringTransaction) => void; paymentMethod: PaymentMethod | null }) {
  const PaymentIcon = item.is_active && paymentMethod
    ? (paymentMethod.type === 'credit_card' ? CreditCard : Landmark)
    : null
  return (
    <div
      className={`flex items-center justify-between rounded-lg px-2 py-3 active:bg-accent/80 cursor-pointer transition-colors ${!item.is_active ? "opacity-50" : ""}`}
      onClick={() => onEdit(item)}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="shrink-0 text-sm text-muted-foreground tabular-nums w-20">
          {getNextOccurrenceDate(item.frequency_months, item.day_of_month)}
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {PaymentIcon && (
              <PaymentIcon
                className="size-3.5 shrink-0 text-muted-foreground"
                aria-label={paymentMethod?.name ?? ''}
              />
            )}
            <span className="truncate text-sm font-medium">{item.name}</span>
            {item.scope === 'personal' && (
              <Badge variant="outline" className="shrink-0 text-xs">
                個人
              </Badge>
            )}
            {!item.is_active && (
              <Badge variant="secondary" className="shrink-0 text-xs">
                停止中
              </Badge>
            )}
          </div>
          <span className="flex items-center gap-1 text-xs text-muted-foreground truncate">
            {item.parent_category_color ? (
              <span
                className="inline-block size-2 shrink-0 rounded-full"
                style={{ backgroundColor: item.parent_category_color }}
              />
            ) : (
              <span className="inline-block size-2 shrink-0 rounded-full border border-muted-foreground" />
            )}
            {item.category_name ?? UNCATEGORIZED_LABEL}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className="text-sm font-medium">
          {formatAmount(item.amount, item.currency)}
        </span>
        <Avatar className="size-5 shrink-0">
          {item.paid_by_avatar_url && (
            <AvatarImage src={item.paid_by_avatar_url} alt={item.paid_by_name ?? ''} />
          )}
          <AvatarFallback className="text-[10px]">
            {item.paid_by_name?.charAt(0) ?? '?'}
          </AvatarFallback>
        </Avatar>
      </div>
    </div>
  )
}

export default function RecurringPage() {
  const { data: categories = [] } = useCategories()
  const { data: paymentMethods = [] } = usePaymentMethods()
  const updateRec = useUpdateRecurring()

  const paymentMethodMap = useMemo(() => {
    const m = new Map<string, PaymentMethod>()
    for (const pm of paymentMethods) m.set(pm.id, pm)
    return m
  }, [paymentMethods])

  const { data: items = [], isLoading: loading } = useRecurring('all')
  const [sortKey, setSortKey] = useRecurringSort()
  const [tab, setTab] = useRecurringTab()

  const categorySortOrder = useMemo(() => {
    const map = new Map<string, number>()
    const orderMap = new Map<string, number>()
    for (const cat of categories) {
      orderMap.set(cat.id, cat.sort_order ?? 0)
    }
    for (const cat of categories) {
      const parentId = cat.parent_id ?? cat.id
      map.set(cat.id, orderMap.get(parentId) ?? Infinity)
    }
    return map
  }, [categories])

  const sortItems = (list: RecurringTransaction[]) => {
    return [...list].sort((a, b) => {
      switch (sortKey) {
        case 'day_of_month': {
          const dateA = getNextOccurrenceDate(a.frequency_months, a.day_of_month)
          const dateB = getNextOccurrenceDate(b.frequency_months, b.day_of_month)
          return dateA.localeCompare(dateB)
        }
        case 'amount':
          return b.amount - a.amount
        case 'category': {
          const orderA = a.category_id ? (categorySortOrder.get(a.category_id) ?? Infinity) : Infinity
          const orderB = b.category_id ? (categorySortOrder.get(b.category_id) ?? Infinity) : Infinity
          return orderA - orderB
        }
        case 'paid_by': {
          const nameA = a.paid_by_name ?? ''
          const nameB = b.paid_by_name ?? ''
          if (!nameA && nameB) return 1
          if (nameA && !nameB) return -1
          return nameA.localeCompare(nameB)
        }
      }
    })
  }

  const monthlyItems = useMemo(() =>
    sortItems(items.filter(i => i.frequency !== 'yearly')),
    [items, sortKey, categorySortOrder],
  )

  const yearlyItems = useMemo(() =>
    sortItems(items.filter(i => i.frequency === 'yearly')),
    [items, sortKey, categorySortOrder],
  )

  const monthlyJpyTotal = useMemo(() => monthlyItems.filter(i => i.currency !== 'USD').reduce((s, i) => s + i.amount, 0), [monthlyItems])
  const monthlyUsdTotal = useMemo(() => monthlyItems.filter(i => i.currency === 'USD').reduce((s, i) => s + i.amount, 0), [monthlyItems])
  const yearlyJpyTotal = useMemo(() => yearlyItems.filter(i => i.currency !== 'USD').reduce((s, i) => s + i.amount, 0), [yearlyItems])
  const yearlyUsdTotal = useMemo(() => yearlyItems.filter(i => i.currency === 'USD').reduce((s, i) => s + i.amount, 0), [yearlyItems])

  const { data: usdToJpyRate } = useUsdToJpyRate()
  const monthlyCombined = usdToJpyRate && monthlyUsdTotal > 0
    ? monthlyJpyTotal + convertUsdToJpy(monthlyUsdTotal, usdToJpyRate)
    : null
  const yearlyCombined = usdToJpyRate && yearlyUsdTotal > 0
    ? yearlyJpyTotal + convertUsdToJpy(yearlyUsdTotal, usdToJpyRate)
    : null

  // Sheet state
  const [sheetOpen, setSheetOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<RecurringTransaction | null>(null)

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<RecurringTransaction | null>(null)

  const handleCreate = () => {
    setEditingItem(null)
    setSheetOpen(true)
  }

  const handleEdit = (item: RecurringTransaction) => {
    setEditingItem(item)
    setSheetOpen(true)
  }

  const handleToggleActive = (item: RecurringTransaction) => {
    updateRec.mutate(
      { id: item.id, data: { is_active: !item.is_active } },
      {
        onSuccess: () => {
          toast.success(item.is_active ? "固定支出を停止しました" : "固定支出を再開しました")
        },
      },
    )
  }

  const handleDeleteRequest = (item: RecurringTransaction) => {
    setDeleteTarget(item)
    setDeleteDialogOpen(true)
  }

  if (loading) {
    return (
      <div className="bg-background">
        <div className="mx-auto max-w-lg px-4 py-6">
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/settings" replace>
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="size-5" />
                </Button>
              </Link>
              <h1 className="text-xl font-semibold">固定費（繰り返しの支出）</h1>
            </div>
            <Button size="sm" onClick={handleCreate}>
              <Plus className="size-4" />
              追加
            </Button>
          </div>
          <div className="divide-y">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between px-2 py-3">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-4 w-10" />
                  <div className="space-y-1">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="size-5 rounded-full" />
                  <Skeleton className="size-4 w-4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-background">
      <div className="mx-auto max-w-lg px-4 py-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/settings" replace>
              <Button variant="ghost" size="icon">
                <ArrowLeft className="size-5" />
              </Button>
            </Link>
            <h1 className="text-xl font-semibold">固定費（繰り返しの支出）</h1>
          </div>
          <Button size="sm" onClick={handleCreate}>
            <Plus className="size-4" />
            追加
          </Button>
        </div>

        {/* List */}
        {items.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            固定支出が登録されていません
          </p>
        ) : (
          <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
            <TabsList className="w-full">
              <TabsTrigger value="monthly">毎月</TabsTrigger>
              <TabsTrigger value="yearly">年間</TabsTrigger>
            </TabsList>
            <TabsContent value="monthly">
              <div className="flex items-center justify-between px-2 py-2">
                <span className="text-xs font-medium text-muted-foreground">
                  合計 {monthlyCombined != null
                    ? formatAmount(monthlyCombined)
                    : formatAmount(monthlyJpyTotal)}
                  {monthlyCombined == null && monthlyUsdTotal > 0 && ` + ${formatAmount(monthlyUsdTotal, 'USD')}`}
                  {monthlyCombined != null && monthlyUsdTotal > 0 && usdToJpyRate && (
                    <span className="ml-1 text-[10px]">({formatAmount(monthlyUsdTotal, 'USD')} @ ¥{usdToJpyRate.toFixed(2)}/$ 含む)</span>
                  )}
                </span>
                <Select
                  value={sortKey}
                  onValueChange={(value: string) => setSortKey(value as RecurringSortKey)}
                >
                  <SelectTrigger className="h-8 w-auto border-0 bg-transparent text-xs shadow-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day_of_month">支払日</SelectItem>
                    <SelectItem value="amount">金額</SelectItem>
                    <SelectItem value="category">カテゴリ</SelectItem>
                    <SelectItem value="paid_by">支払者</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between px-2 pb-1">
                <div className="flex items-center gap-3">
                  <span className="shrink-0 text-xs text-muted-foreground w-20">次の支払日</span>
                  <span className="text-xs text-muted-foreground">項目</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">金額</span>
                  <span className="text-xs text-muted-foreground w-5 text-center">誰</span>
                </div>
              </div>
              <div className="divide-y">
                {monthlyItems.map((item) => (
                  <RecurringListItem key={item.id} item={item} onEdit={handleEdit} paymentMethod={item.payment_method_id ? (paymentMethodMap.get(item.payment_method_id) ?? null) : null} />
                ))}
              </div>
            </TabsContent>
            <TabsContent value="yearly">
              <div className="flex items-center justify-between px-2 py-2">
                <span className="text-xs font-medium text-muted-foreground">
                  合計 {yearlyCombined != null
                    ? formatAmount(yearlyCombined)
                    : formatAmount(yearlyJpyTotal)}
                  {yearlyCombined == null && yearlyUsdTotal > 0 && ` + ${formatAmount(yearlyUsdTotal, 'USD')}`}
                  {yearlyCombined != null && yearlyUsdTotal > 0 && usdToJpyRate && (
                    <span className="ml-1 text-[10px]">({formatAmount(yearlyUsdTotal, 'USD')} @ ¥{usdToJpyRate.toFixed(2)}/$ 含む)</span>
                  )}
                </span>
                <Select
                  value={sortKey}
                  onValueChange={(value: string) => setSortKey(value as RecurringSortKey)}
                >
                  <SelectTrigger className="h-8 w-auto border-0 bg-transparent text-xs shadow-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day_of_month">支払日</SelectItem>
                    <SelectItem value="amount">金額</SelectItem>
                    <SelectItem value="category">カテゴリ</SelectItem>
                    <SelectItem value="paid_by">支払者</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between px-2 pb-1">
                <div className="flex items-center gap-3">
                  <span className="shrink-0 text-xs text-muted-foreground w-20">次の支払日</span>
                  <span className="text-xs text-muted-foreground">項目</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">金額</span>
                  <span className="text-xs text-muted-foreground w-5 text-center">誰</span>
                </div>
              </div>
              <div className="divide-y">
                {yearlyItems.map((item) => (
                  <RecurringListItem key={item.id} item={item} onEdit={handleEdit} paymentMethod={item.payment_method_id ? (paymentMethodMap.get(item.payment_method_id) ?? null) : null} />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>

      <RecurringEditSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        editingItem={editingItem}
        onDelete={handleDeleteRequest}
        onToggleActive={handleToggleActive}
      />

      <DeleteRecurringDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        item={deleteTarget}
      />
    </div>
  )
}
